/**
 * SDR Dual Panel Interface
 *
 * Beautiful, mobile-responsive dual panel interface where users can:
 * - Chat with their AI SDR as a manager (left/top panel)
 * - Simulate lead conversations (right/bottom panel)
 * - See real-time escalations and learning
 * - Seamlessly start new lead conversations
 */

import type { Message } from "@ai-sdk/react";
import { Button } from "@client/components/button/Button";
import { Card } from "@client/components/card/Card";
import { MemoizedMarkdown } from "@client/components/memoized-markdown";
import { Textarea } from "@client/components/textarea/Textarea";
import { useCreateSDRLead, useLastCreatedLead, useScrollToBottom } from "@client/hooks";
import { useAgentChat } from "agents/ai-react";
import { useAgent } from "agents/react";
import { Suspense, useEffect, useState } from "react";
import { WaitlistButton } from "./auth/WaitlistButton";

interface DualPanelProps {
	sdrId: string;
	onBack: () => void;
}

// Panel Header Component
interface PanelHeaderProps {
	icon: string;
	title: string;
	description: string;
	actionButton?: React.ReactNode;
	className?: string;
	compact?: boolean;
}

function PanelHeader({ icon, title, description, actionButton, className = "", compact = false }: PanelHeaderProps) {
	return (
		<div className={`flex-shrink-0 ${compact ? 'px-1 py-3' : 'p-6'} border-b border-gray-700/50 ${className}`}>
			<div className="flex items-center justify-between">
				<div className={`flex items-center gap-4 ${compact ? 'pl-2' : 'pl-0'}`}>
					<div className={`w-${compact ? '8' : '12'} h-${compact ? '8' : '12'} bg-gradient-to-r ${title.includes('Manager') ? 'from-indigo-500 to-indigo-600' : 'from-purple-500 to-purple-600'} rounded-full flex items-center justify-center`}>
						<span className={`text-white font-semibold ${compact ? 'text-sm' : 'text-lg'}`}>{icon}</span>
					</div>
					<div>
						<h3 className={`${compact ? 'font-semibold' : 'text-xl font-bold'} text-white`}>{title}</h3>
						<p className={`${compact ? 'text-xs' : 'text-base'} text-gray-400`}>{description}</p>
					</div>
				</div>
				{actionButton}
			</div>
		</div>
	);
}

// Empty State Component
interface EmptyStateProps {
	icon: string;
	title: string;
	description: string;
	actionButton?: React.ReactNode;
	compact?: boolean;
}

function EmptyState({ icon, title, description, actionButton, compact = false }: EmptyStateProps) {
	return (
		<div className={`flex flex-col items-center justify-center text-center text-gray-400 py-${compact ? '8' : '16'}`}>
			<div className={`${compact ? 'text-4xl' : 'text-8xl'} mb-${compact ? '2' : '6'}`}>{icon}</div>
			<p className={`${compact ? 'mb-1' : 'text-2xl mb-3'}`}>{title}</p>
			<p className={`${compact ? 'text-sm' : 'text-lg'} ${actionButton ? (compact ? 'mb-6' : 'mb-10') : ''}`}>
				{description}
			</p>
			{actionButton}
		</div>
	);
}

// Message List Component
interface MessageListProps {
	messages: Message[];
	type: 'manager' | 'lead';
	compact?: boolean;
}

function MessageList({ messages, type, compact = false }: MessageListProps) {

	// Use the new scroll to bottom hook
	const { scrollRef, endRef } = useScrollToBottom({
		threshold: 50,
		dependencies: [messages.length]
	});

	return (
		<div 
			ref={scrollRef}
			className={`flex-1 ${compact ? 'px-0 py-0' : 'p-6'} overflow-y-auto space-y-${compact ? '3' : '4'} min-h-0`}
		>
			{messages.map((message) => {
				const isUser = message.role === "user";
				const colorClass = type === 'manager' ? 'indigo' : 'purple';

				if (!message.content) return null;

				return (
					<div
						key={message.id}
						className={`flex ${isUser ? "justify-end" : "justify-start"}`}
					>
						<div
							className={`flex gap-${compact ? '2' : '4'} max-w-[90%] ${isUser ? "flex-row-reverse" : "flex-row"}`}
						>


							<div className="flex-1">
															<div
								className={`${compact ? 'p-3' : 'p-[15px]'} rounded-2xl ${
									isUser
										? `bg-${colorClass}-600 text-white rounded-br-md`
										: "bg-gray-700/50 text-gray-100 rounded-bl-md"
								}`}
							>
									<MemoizedMarkdown
										id={`${type}-${message.id}`}
										content={message.content}
									/>
								</div>

							</div>
						</div>
					</div>
				);
			})}
			{/* Scroll anchor element */}
			<div ref={endRef} />
		</div>
	);
}

// Chat Input Component
interface ChatInputProps {
	value: string;
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	onSubmit: (e: React.FormEvent) => void;
	placeholder: string;
	isLoading: boolean;
	type: 'manager' | 'lead';
	compact?: boolean;
	showHint?: boolean;
}

function ChatInput({ value, onChange, onSubmit, placeholder, isLoading, type, compact = false, showHint = false }: ChatInputProps) {
	const colorClass = type === 'manager' ? 'indigo' : 'purple';
	const minHeight = compact ? '44px' : '50px';
	const maxHeight = compact ? '100px' : '120px';

	return (
		<div className={`flex-shrink-0 ${compact ? 'pb-1 px-1' : 'p-3'} border-t border-gray-700/50`}>
			{showHint && !compact && (
				<div className="mb-3 hidden md:block">
					<div className="flex flex-wrap gap-2 text-gray-400 text-sm">
						Try asking the AI SDR for something it doesn't know
					</div>
				</div>
			)}

			<form onSubmit={onSubmit} className={`flex gap-${compact ? '2' : '3'} items-end ${compact ? 'pt-2' : ''}`}>
				<Textarea
					value={value}
					onChange={onChange}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							onSubmit(e);
						}
					}}
					placeholder={placeholder}
					className={`flex-1 bg-gray-700/50 border-gray-600/50 text-white placeholder-gray-400 focus:border-${colorClass}-400 focus:ring-${colorClass}-400/20 min-h-[${minHeight}] max-h-[${maxHeight}] resize-none rounded-${compact ? 'lg' : 'xl'} text-base p-${compact ? '3' : '4'} overflow-y-auto`}
					rows={1}
					style={{
						height: 'auto',
						minHeight: minHeight,
						maxHeight: maxHeight
					}}
					ref={(textarea) => {
						if (textarea) {
							textarea.style.height = 'auto';
							textarea.style.height = Math.min(textarea.scrollHeight, parseInt(maxHeight)) + 'px';
						}
					}}
				/>
				<Button
					type="submit"
					disabled={isLoading || !value.trim()}
					className={`bg-${colorClass}-600 hover:bg-${colorClass}-500 text-white px-${compact ? '4' : '6'} ${compact ? 'rounded-lg min-h-[44px]' : 'py-3 rounded-xl'} transition-colors ${compact ? '' : 'font-semibold'} self-end`}
				>
					{isLoading ? "..." : "Send"}
				</Button>
			</form>
		</div>
	);
}

// Chat Panel Component
interface ChatPanelProps {
	type: 'manager' | 'lead';
	messages: Message[];
	input: string;
	onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	onSubmit: (e: React.FormEvent) => void;
	isLoading: boolean;
	emptyState?: React.ReactNode;
	actionButton?: React.ReactNode;
	compact?: boolean;
	showInputHint?: boolean;
	showInput?: boolean;
}

function ChatPanel({ 
	type, 
	messages, 
	input, 
	onInputChange, 
	onSubmit, 
	isLoading, 
	emptyState, 
	actionButton,
	compact = false,
	showInputHint = false,
	showInput = true
}: ChatPanelProps) {
	// Remove the old scroll implementation
	
	const config = {
		manager: {
			icon: "👨‍💼",
			title: "Manager Chat",
			description: "Guide your AI SDR when it needs help",
			placeholder: compact ? "Guide your AI SDR..." : "Provide guidance to your AI SDR..."
		},
		lead: {
			icon: "🎯",
			title: "Lead Simulation",
			description: "Test your AI SDR with prospect conversations",
			placeholder: compact ? "Chat as potential customer..." : "Chat as a potential customer..."
		}
	};

	return (
		<Card className={`flex flex-col ${compact ? 'h-[calc(100dvh-120px)] p-0' : ''} overflow-hidden bg-gray-800/50 border-gray-700/50 backdrop-blur-sm ${compact ? '' : 'shadow-xl'}`}>
			<PanelHeader
				icon={config[type].icon}
				title={config[type].title}
				description={config[type].description}
				actionButton={actionButton}
				compact={compact}
			/>

			<div className="flex-1 overflow-hidden flex flex-col">
				{messages.length === 0 && emptyState ? (
					<div className="flex-1 flex items-center justify-center">
						{emptyState}
					</div>
				) : (
					<MessageList messages={messages} type={type} compact={compact} />
				)}
			</div>

			{showInput && (
				<ChatInput
					value={input}
					onChange={onInputChange}
					onSubmit={onSubmit}
					placeholder={config[type].placeholder}
					isLoading={isLoading}
					type={type}
					compact={compact}
					showHint={showInputHint}
				/>
			)}
		</Card>
	);
}

export function SDRDualPanel({ sdrId, onBack }: DualPanelProps) {
	return (
		<Suspense fallback={null}>
			<SDRDualPanelFull sdrId={sdrId} onBack={onBack} />
		</Suspense>
	);
}

function SDRDualPanelFull({ sdrId, onBack }: DualPanelProps) {
	const [leadId, setLeadId] = useState<string | null>(null);
	const [isCreatingLead, setIsCreatingLead] = useState(false);
	const [activeTab, setActiveTab] = useState<'manager' | 'lead'>('manager');
	
	// Tab notification badges - track unread message counts
	const [tabBadges, setTabBadges] = useState({
		manager: 0,
		lead: 0,
	});
	
	// Track if user has seen the initial invitation
	const [hasSeenInvitation, setHasSeenInvitation] = useState(false);

	// Enhanced attention mechanisms for mobile
	const [showTabPulse, setShowTabPulse] = useState(true);
	const [showSlidingTeaser, setShowSlidingTeaser] = useState(false);
	const [showTabHint, setShowTabHint] = useState(true);
	const [hasInteractedWithLeadTab, setHasInteractedWithLeadTab] = useState(false);

	// Manager chat
	const managerAgent = useAgent({
		agent: "manager-agent",
		name: sdrId,
	});

	const {
		messages: managerMessages,
		input: managerInput,
		handleInputChange: handleManagerInputChange,
		handleSubmit: handleManagerSubmit,
		isLoading: managerLoading,
	} = useAgentChat({
		agent: managerAgent,
		maxSteps: 5,
	});

	// Lead chat - only create agent when leadId exists
	const leadAgent = useAgent({
		agent: "lead-agent",
		name: leadId ? `sdr-${sdrId}-lead-${leadId}` : undefined,
	});

	const leadChatResult = useAgentChat({
		id: leadId || undefined,
		agent: leadAgent,
		maxSteps: 5,
	});

	// Only use lead chat when leadId exists, otherwise provide empty defaults
	const {
		messages: leadMessages,
		input: leadInput,
		handleInputChange: handleLeadInputChange,
		handleSubmit: handleLeadSubmit,
		isLoading: leadLoading,
	} = leadId ? leadChatResult : {
		messages: [],
		input: "",
		handleInputChange: () => {},
		handleSubmit: () => {},
		isLoading: false,
	};

	// Hooks for lead management
	const createLeadMutation = useCreateSDRLead(sdrId);
	const lastCreatedLead = useLastCreatedLead(sdrId);

	console.log("🔍 lastCreatedLead", lastCreatedLead);

	// Create new lead conversation
	const createLeadConversation = async () => {
		setIsCreatingLead(true);
		try {
			const result = await createLeadMutation.mutate({});
			if (result) {
				setLeadId(result.leadId);
				// Enhanced mobile experience: automatically switch to lead tab and mark as interacted
				setActiveTab('lead');
				setHasInteractedWithLeadTab(true);
				setShowTabPulse(false);
				setShowSlidingTeaser(false);
				setShowTabHint(false);
				// Refresh the last created lead data to keep it in sync
				lastCreatedLead.refetch();
			}
		} catch (error) {
			console.error("Error creating lead conversation:", error);
		} finally {
			setIsCreatingLead(false);
		}
	};

	// Load last created lead on component mount
	useEffect(() => {
		if (lastCreatedLead.data?.leads && lastCreatedLead.data.leads.length > 0) {
			const latestLead = lastCreatedLead.data.leads[0];
			if (!leadId) {
				setLeadId(latestLead.leadId);
			}
		}
	}, [lastCreatedLead.data, leadId]);

	// Monitor manager messages for badge counts
	const [previousManagerCount, setPreviousManagerCount] = useState(0);
	useEffect(() => {
		if (managerMessages.length > previousManagerCount && previousManagerCount > 0) {
			// New manager message arrived
			if (activeTab !== 'manager') {
				const newMessages = managerMessages.length - previousManagerCount;
				setTabBadges(prev => ({ ...prev, manager: prev.manager + newMessages }));
			}
		}
		setPreviousManagerCount(managerMessages.length);
	}, [managerMessages.length, activeTab, previousManagerCount]);

	// Monitor lead messages for badge counts
	const [previousLeadCount, setPreviousLeadCount] = useState(0);
	useEffect(() => {
		if (leadMessages.length > previousLeadCount && previousLeadCount > 0) {
			// New lead message arrived
			if (activeTab !== 'lead') {
				const newMessages = leadMessages.length - previousLeadCount;
				setTabBadges(prev => ({ ...prev, lead: prev.lead + newMessages }));
			}
		}
		setPreviousLeadCount(leadMessages.length);
	}, [leadMessages.length, activeTab, previousLeadCount]);

	// Handle tab switching - reset badge count and mark invitation as seen
	const handleTabSwitch = (tab: 'manager' | 'lead') => {
		setActiveTab(tab);
		setTabBadges(prev => ({ ...prev, [tab]: 0 }));
		if (tab === 'lead') {
			setHasSeenInvitation(true);
			setHasInteractedWithLeadTab(true);
			// Turn off all attention mechanisms once user visits lead tab
			setShowTabPulse(false);
			setShowSlidingTeaser(false);
			setShowTabHint(false);
		}
	};

	// Enhanced attention mechanism timing
	useEffect(() => {
		if (hasInteractedWithLeadTab) return;

		const timers: NodeJS.Timeout[] = [];

		// Show sliding teaser after 3 seconds
		timers.push(setTimeout(() => {
			setShowSlidingTeaser(true);
		}, 3000));

		// Hide sliding teaser after 8 seconds
		timers.push(setTimeout(() => {
			setShowSlidingTeaser(false);
		}, 8000));

		// Show sliding teaser again after 15 seconds if still not interacted
		timers.push(setTimeout(() => {
			if (!hasInteractedWithLeadTab) {
				setShowSlidingTeaser(true);
			}
		}, 15000));

		// Hide sliding teaser again after 20 seconds
		timers.push(setTimeout(() => {
			setShowSlidingTeaser(false);
		}, 20000));

		// Stop tab pulse after 25 seconds
		timers.push(setTimeout(() => {
			setShowTabPulse(false);
		}, 25000));

		// Stop tab hint after 30 seconds
		timers.push(setTimeout(() => {
			setShowTabHint(false);
		}, 30000));

		return () => {
			timers.forEach(timer => clearTimeout(timer));
		};
	}, [hasInteractedWithLeadTab]);

	// Show initial invitation for Lead Sim tab
	useEffect(() => {
		const timer = setTimeout(() => {
			if (!hasSeenInvitation && activeTab === 'manager') {
				setTabBadges(prev => ({ ...prev, lead: 1 }));
			}
		}, 1500); // Show invitation after 1.5 seconds

		return () => clearTimeout(timer);
	}, [hasSeenInvitation, activeTab]);

	// Add custom scrollbar styles and notification badge animations
	useEffect(() => {
		const style = document.createElement('style');
		style.textContent = `
			/* Modern scrollbar styling */
			::-webkit-scrollbar {
				width: 8px;
				height: 8px;
			}
			::-webkit-scrollbar-track {
				background: rgba(55, 65, 81, 0.3);
				border-radius: 4px;
			}
			::-webkit-scrollbar-thumb {
				background: rgba(156, 163, 175, 0.5);
				border-radius: 4px;
			}
			::-webkit-scrollbar-thumb:hover {
				background: rgba(156, 163, 175, 0.7);
			}
			/* Firefox */
			* {
				scrollbar-width: thin;
				scrollbar-color: rgba(156, 163, 175, 0.5) rgba(55, 65, 81, 0.3);
			}
			
			/* Enhanced notification badge animations */
			@keyframes notification-bounce {
				0%, 20%, 50%, 80%, 100% {
					transform: translateY(0) scale(1);
				}
				40% {
					transform: translateY(-3px) scale(1.1);
				}
				60% {
					transform: translateY(-1px) scale(1.05);
				}
			}
			
			.notification-badge {
				animation: notification-bounce 2s ease-in-out infinite;
				box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.3);
			}

			/* Enhanced tab attention animations */
			@keyframes tab-pulse {
				0% {
					box-shadow: 0 0 0 0 rgba(147, 51, 234, 0.7);
					background: rgba(147, 51, 234, 0.1);
				}
				50% {
					box-shadow: 0 0 0 8px rgba(147, 51, 234, 0.1);
					background: rgba(147, 51, 234, 0.2);
				}
				100% {
					box-shadow: 0 0 0 0 rgba(147, 51, 234, 0);
					background: rgba(147, 51, 234, 0.1);
				}
			}

			@keyframes tab-glow {
				0%, 100% {
					box-shadow: 0 0 5px rgba(147, 51, 234, 0.5);
				}
				50% {
					box-shadow: 0 0 20px rgba(147, 51, 234, 0.8), 0 0 30px rgba(147, 51, 234, 0.6);
				}
			}

			.tab-attention {
				animation: tab-pulse 2s ease-in-out infinite, tab-glow 3s ease-in-out infinite;
				position: relative;
			}

			.tab-attention::before {
				content: '';
				position: absolute;
				top: -2px;
				left: -2px;
				right: -2px;
				bottom: -2px;
				background: linear-gradient(45deg, rgba(147, 51, 234, 0.3), rgba(168, 85, 247, 0.3));
				border-radius: 4px;
				z-index: -1;
			}

			/* Sliding teaser animation */
			@keyframes slide-teaser {
				0% {
					transform: translateX(100%);
					opacity: 0;
				}
				15% {
					transform: translateX(0);
					opacity: 1;
				}
				85% {
					transform: translateX(0);
					opacity: 1;
				}
				100% {
					transform: translateX(100%);
					opacity: 0;
				}
			}

			.sliding-teaser {
				animation: slide-teaser 5s ease-in-out;
			}

			/* Tab hint bounce */
			@keyframes hint-bounce {
				0%, 20%, 50%, 80%, 100% {
					transform: translateY(0);
				}
				40% {
					transform: translateY(-4px);
				}
				60% {
					transform: translateY(-2px);
				}
			}

			.tab-hint {
				animation: hint-bounce 2s ease-in-out infinite;
			}
		`;
		document.head.appendChild(style);
		return () => {
			document.head.removeChild(style);
		};
	}, []);

	// Manager empty state
	const managerEmptyState = (
		<EmptyState
			icon="🤖"
			title="Your AI SDR manager is ready!"
			description="Start a lead conversation to see escalations here."
		/>
	);

	// Lead empty states
	const leadEmptyState = !leadId ? (
		<EmptyState
			icon="💬"
			title="Start a lead conversation"
			description="Test how your AI SDR handles prospect interactions"
			actionButton={
				<Button
					onClick={createLeadConversation}
					disabled={isCreatingLead}
					size="lg"
					className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white font-bold px-10 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
				>
					{isCreatingLead ? (
						<span className="flex items-center gap-2">
							<div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
							Creating...
						</span>
					) : (
						"Start chatting as Lead"
					)}
				</Button>
			}
		/>
	) : leadMessages.length === 0 ? (
		<EmptyState
			icon="🚀"
			title="Lead conversation ready!"
			description="Start chatting as a potential customer"
		/>
	) : null;

	// Mobile lead empty state (more compact)
	const leadEmptyStateMobile = !leadId ? (
		<EmptyState
			icon="💬"
			title="Start a lead conversation"
			description="Test how your AI SDR handles prospect interactions"
			compact
			actionButton={
				<Button
					onClick={createLeadConversation}
					disabled={isCreatingLead}
					className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
				>
					{isCreatingLead ? (
						<span className="flex items-center gap-2">
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
							Creating...
						</span>
					) : (
						"Start chatting as Lead"
					)}
				</Button>
			}
		/>
	) : leadMessages.length === 0 ? (
		<EmptyState
			icon="🚀"
			title="Lead conversation ready!"
			description="Start chatting as a potential customer"
			compact
		/>
	) : null;

	return (
		<div className="min-h-dvh bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
			{/* Compact Header */}
			<div className="bg-gray-900/80 backdrop-blur-md border-b border-gray-700/50">
				<div className="max-w-8xl mx-auto px-1 sm:px-6 lg:px-8">
					<div className="flex items-center justify-between py-2 sm:py-4">
						<div className="flex items-center gap-6">
							<Button 
								onClick={onBack}
								className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
							>
								← New SDR
							</Button>
							<h1 className="hidden sm:block text-2xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
								AI SDR Training Interface
							</h1>
						</div>
						
						<div className="sm:flex items-center gap-3 text-sm text-gray-400">
							<WaitlistButton />
						</div>
					</div>
				</div>
			</div>

			{/* Mobile Tab Navigation */}
			<div className="lg:hidden bg-gray-800/50 border-b border-gray-700/50 relative">
				{/* Sliding Teaser */}
				{showSlidingTeaser && (
					<div className="absolute top-full left-0 right-0 z-50 pointer-events-none">
						<div className="sliding-teaser bg-gradient-to-r from-purple-600 to-purple-500 text-white text-sm font-medium px-4 py-2 mx-4 mt-2 rounded-lg shadow-lg flex items-center gap-2">
							<span>🎯</span>
							<span>Try chatting as a customer!</span>
							<span className="ml-auto">→</span>
						</div>
					</div>
				)}

				{/* Tab Hint */}
				{showTabHint && !hasInteractedWithLeadTab && (
					<div className="absolute -top-8 right-4 z-40 pointer-events-none">
						<div className="tab-hint bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
							Try this! ✨
						</div>
					</div>
				)}

				<div className="max-w-8xl mx-auto px-0">
					<div className="flex relative">
						<button
							onClick={() => handleTabSwitch('manager')}
							className={`flex-1 py-3 text-center font-medium transition-all duration-200 text-sm relative ${
								activeTab === 'manager'
									? 'text-indigo-400 border-b-2 border-indigo-400 bg-indigo-400/10'
									: 'text-gray-400 hover:text-gray-200'
							}`}
						>
							<span className="relative inline-flex items-center">
								👨‍💼 Manager
								{tabBadges.manager > 0 && activeTab !== 'manager' && (
									<span className="absolute -top-2 -right-2 min-w-[20px] h-5 bg-green-500 text-white text-xs font-bold rounded-full flex items-center justify-center notification-badge border-2 border-gray-800">
										{tabBadges.manager > 99 ? '99+' : tabBadges.manager}
									</span>
								)}
							</span>
						</button>
						<button
							onClick={() => handleTabSwitch('lead')}
							className={`flex-1 py-3 text-center font-medium transition-all duration-200 text-sm relative ${
								activeTab === 'lead'
									? 'text-purple-400 border-b-2 border-purple-400 bg-purple-400/10'
									: 'text-gray-400 hover:text-gray-200'
							} ${showTabPulse && !hasInteractedWithLeadTab ? 'tab-attention' : ''}`}
						>
							<span className="relative inline-flex items-center">
								🎯 Lead Sim
								{tabBadges.lead > 0 && activeTab !== 'lead' && (
									<span className="absolute -top-2 -right-2 min-w-[20px] h-5 bg-green-500 text-white text-xs font-bold rounded-full flex items-center justify-center notification-badge border-2 border-gray-800">
										{tabBadges.lead > 99 ? '99+' : tabBadges.lead}
									</span>
								)}
							</span>
						</button>
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div className="max-w-8xl mx-auto px-0 sm:px-6 lg:px-8 py-2 sm:py-4">
				{/* Desktop Dual Panel Layout */}
				<div className="hidden lg:grid lg:grid-cols-2 gap-8 h-[calc(100dvh-140px)]">
					{/* Manager Panel */}
					<ChatPanel
						type="manager"
						messages={managerMessages}
						input={managerInput}
						onInputChange={handleManagerInputChange}
						onSubmit={handleManagerSubmit}
						isLoading={managerLoading}
						emptyState={managerMessages.length === 0 ? managerEmptyState : undefined}
					/>

					{/* Lead Panel */}
					<ChatPanel
						type="lead"
						messages={leadMessages}
						input={leadInput}
						onInputChange={handleLeadInputChange}
						onSubmit={handleLeadSubmit}
						isLoading={leadLoading}
						emptyState={leadEmptyState}
						actionButton={leadId ? (
							<Button
								onClick={createLeadConversation}
								disabled={isCreatingLead}
								className="bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-lg transition-colors font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
							>
								{isCreatingLead ? "..." : "New Chat"}
							</Button>
						) : undefined}
						showInputHint={leadId ? true : false}
						showInput={leadId ? true : false}
					/>
				</div>

				{/* Mobile Stacked Layout */}
				<div className="lg:hidden">
					{activeTab === 'manager' && (
						<ChatPanel
							type="manager"
							messages={managerMessages}
							input={managerInput}
							onInputChange={handleManagerInputChange}
							onSubmit={handleManagerSubmit}
							isLoading={managerLoading}
							emptyState={managerMessages.length === 0 ? (
								<EmptyState
									icon="🤖"
									title="Your AI SDR manager is ready!"
									description="Start a lead conversation to see escalations here."
									compact
								/>
							) : undefined}
							compact
						/>
					)}

					{activeTab === 'lead' && (
						<ChatPanel
							type="lead"
							messages={leadMessages}
							input={leadInput}
							onInputChange={handleLeadInputChange}
							onSubmit={handleLeadSubmit}
							isLoading={leadLoading}
							emptyState={leadEmptyStateMobile}
							actionButton={leadId ? (
								<Button
									onClick={createLeadConversation}
									disabled={isCreatingLead}
									size="sm"
									className="bg-purple-600 hover:bg-purple-500 text-white px-3 py-1 rounded-lg transition-colors text-sm"
								>
									{isCreatingLead ? "..." : "New"}
								</Button>
							) : undefined}
							compact
							showInput={leadId ? true : false}
						/>
					)}
				</div>
			</div>
		</div>
	);
}
